import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ProductCard from "../components/product/ProductCard";
import AutoplayVideo from "../components/AutoplayVideo";
import { PROCESS_VIDEO, PROCESS_POSTER } from "../data/products";
import { useStore } from "../context/StoreContext";

const EXCLUSIVE_FILTERS = [
  { key: "all", label: "All exclusives" },
  { key: "limited", label: "1 of 1s" },
  { key: "limited", label: "Limited pieces" },
  { key: "upcycled", label: "Upcycled / restored" },
];

const STATS = [
  {
    n: "01",
    title: "SCARCITY",
    body: "Each piece is made once. Once it's gone, it's gone. We do not restock.",
  },
  {
    n: "02",
    title: "PROCESS",
    body: "Hours of cutting, dyeing, distressing, hand-finishing. No mass production.",
  },
  {
    n: "03",
    title: "STORY",
    body: "Built from materials with a past. Vintage denim, salvaged leather, rescued cloth.",
  },
];

const ExclusivesPage = () => {
  const [filter, setFilter] = useState("all");
  const { products } = useStore();

  const heroImg = useMemo(() => {
    const sd = products.find((p) => p.id === "speed-demon");
    return sd?.images?.[0];
  }, [products]);

  const exclusives = useMemo(
    () =>
      products
        .filter((p) => p.category === "EXCLUSIVE")
        .filter((p) => (filter === "all" ? true : (p.tags || []).includes(filter))),
    [products, filter]
  );

  return (
    <div data-testid="page-exclusives">
      {/* A. Hero */}
      <section className="relative w-full h-[90svh] min-h-[560px] overflow-hidden">
        <img
          src={heroImg}
          alt="Exclusives"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/30 to-ink/85" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 pt-[60px]">
          <h1 className="font-display uppercase text-6xl sm:text-8xl md:text-9xl text-cream leading-none">
            Exclusives
          </h1>
          <p className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-cream/80 mt-4">
            1 OF 1S / LIMITED / UPCYCLED
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 px-4 md:px-10 pb-8 md:pb-10 bg-gradient-to-t from-ink/90 to-transparent">
          <p className="max-w-2xl font-body text-lg md:text-xl text-cream/90 leading-relaxed">
            Pieces made once and never again. Reworked from vintage materials, hand-finished, signed.
          </p>
        </div>
      </section>

      {/* B. Why are they exclusive? */}
      <section className="px-4 md:px-12 py-16 md:py-24 max-w-[1440px] mx-auto">
        <h2 className="font-display uppercase text-4xl sm:text-6xl md:text-7xl leading-none mb-12">
          Why are they<br />exclusive?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {STATS.map((s) => (
            <div
              key={s.n}
              data-testid={`exclusives-stat-${s.n}`}
              className="bg-charcoal text-cream p-6 md:p-10"
            >
              <p className="font-mono text-xs uppercase tracking-widest text-cream/60">
                {s.n} / {s.title}
              </p>
              <h3 className="font-display uppercase text-3xl md:text-4xl mt-4 leading-tight">
                {s.title}
              </h3>
              <p className="font-body text-lg mt-4 text-cream/85 leading-relaxed">
                {s.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* C. The time & effort */}
      <section className="px-4 md:px-12 pb-16 md:pb-24 max-w-[1440px] mx-auto">
        <h2 className="font-display uppercase text-4xl sm:text-6xl md:text-7xl leading-none mb-8">
          The time<br />&amp; effort.
        </h2>
        <div className="relative w-full rounded-[20px] shadow-card bg-ink overflow-hidden" style={{ aspectRatio: "16/9" }}>
          <AutoplayVideo
            src={PROCESS_VIDEO}
            poster={PROCESS_POSTER}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
        <p className="font-mono text-xs uppercase tracking-widest mt-4 text-ink/70">
          THE PROCESS / RAW WORKSHOP CUT
        </p>
      </section>

      {/* D. Shop Exclusives */}
      <section className="bg-cream-soft px-4 md:px-12 py-16 md:py-24">
        <div className="max-w-[1440px] mx-auto">
          <h2 className="font-display uppercase text-4xl sm:text-6xl md:text-7xl mb-8">
            Shop all exclusives
          </h2>
          <div className="flex gap-2 mb-10 overflow-x-auto hide-scrollbar">
            {EXCLUSIVE_FILTERS.map((f, i) => (
              <button
                key={`${f.key}-${f.label}`}
                onClick={() => setFilter(f.key)}
                data-testid={`exclusives-filter-${i}`}
                className={`dc-pill ${filter === f.key ? "is-active" : ""}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {exclusives.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* E. Custom pieces CTA */}
      <section className="bg-charcoal text-cream px-4 md:px-12 py-16 md:py-24">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 items-end">
          <div>
            <h2 className="font-display uppercase text-5xl sm:text-7xl md:text-8xl leading-none">
              Want your own?
            </h2>
            <p className="font-body text-lg md:text-xl mt-5 text-cream/80 max-w-xl">
              We take a small number of custom commissions each season. Tell us what you want destroyed.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="exclusives-reach-out"
            className="inline-block font-display uppercase text-xl md:text-2xl border border-cream text-cream px-8 py-4 hover:bg-dc-red hover:border-dc-red transition-colors whitespace-nowrap"
          >
            Reach out &rarr;
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ExclusivesPage;
